import { ImgID } from "../../image_loader/img_id.js";
import { TextStyleScheme } from "./text_style_scheme.js";
import { UIElementHolder } from "./ui_element_holder.js";
import { UIImage } from "./ui_image.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "./ui_text_element.js";

export interface RewardFrameOptions
{
  icon_id: ImgID;
  amount_text: string;
  variant?: RewardFrameVariant;
  icon_width?: number;
  icon_height?: number;
}

export type RewardFrameVariant = "large" | "compact";

/** Shared reward card whose layout and styling come from the NoAds offer. */
export class RewardFrame extends UIElementHolder
{
  /** Native reward size used by GetRewardOverlay and reward popups. */
  public static readonly DESIGN_W = 228;
  public static readonly DESIGN_H = 282;
  /** Smaller NoAds grid variant. */
  public static readonly COMPACT_DESIGN_W = 163;
  public static readonly COMPACT_DESIGN_H = 202;

  // Content coordinates are authored against the compact NoAds card and are
  // scaled together with the frame for the large reward-overlay variant.
  private static readonly CONTENT_BASE_W = 163;
  private static readonly CONTENT_BASE_H = 202;
  private static readonly ICON_AREA_H = 150;
  private static readonly ICON_TOP = 10;
  private static readonly AMOUNT_TOP = 115;
  private static readonly AMOUNT_H = 65;

  public readonly background: UIImage;
  public readonly icon: UIImage;
  public readonly amount: UITextElement;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    options: RewardFrameOptions
  )
  {
    const compact = options.variant === "compact";
    const design_w = compact ? RewardFrame.COMPACT_DESIGN_W : RewardFrame.DESIGN_W;
    const design_h = compact ? RewardFrame.COMPACT_DESIGN_H : RewardFrame.DESIGN_H;
    const width = LayoutScaler.widthFromDesign( design_w );
    const height = LayoutScaler.heightFromDesign( design_h );
    super( ctx, x, y, width, height, false );

    const scale_x = width / RewardFrame.CONTENT_BASE_W;
    const scale_y = height / RewardFrame.CONTENT_BASE_H;
    const icon_area_h = RewardFrame.ICON_AREA_H * scale_y;
    const icon_w = options.icon_width ?? RewardFrame.ICON_AREA_H * scale_x;
    const icon_h = options.icon_height ?? RewardFrame.ICON_AREA_H * scale_y;

    this.background = new UIImage( ctx, 0, 0, width, height, ImgID.UI_CoinBg );
    this.icon = new UIImage(
      ctx,
      ( width - icon_w ) / 2,
      RewardFrame.ICON_TOP * scale_y + ( icon_area_h - icon_h ) / 2,
      icon_w,
      icon_h,
      options.icon_id
    );
    this.amount = new UITextElement(
      ctx,
      0,
      RewardFrame.AMOUNT_TOP * scale_y,
      width,
      RewardFrame.AMOUNT_H * scale_y,
      {
        text: options.amount_text,
        font_size: LayoutScaler.textSizeFromDesign( 54 ),
        font_weight: 900,
        text_style: TextStyleScheme.rubik,
        text_color: "#FFFFFF",
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 9 ),
        outline_color: "#02091C",
        auto_wrap: false,
        scale_to_width: true,
        min_font_size: LayoutScaler.textSizeFromDesign( 30 ),
      }
    );

    this.addChild( this.background, true );
    this.addChild( this.icon, true );
    this.addChild( this.amount, true );
  }

  public override name(): string { return "RewardFrame"; }
}
